import { useMutation, type QueryClient } from "@tanstack/react-query";
import { captureException } from "@sentry/react";
import { isAxiosError } from "axios";
import { apiClient, signApiRequest } from "./";
import type { CustomerCredentials } from "../ui/customerDataStore";

export interface AppEngagement {
  ctaClickedAt?: string;
  insurelyInitiatedAt?: string;
}

// The POA journey has no lead, so this lands on the customer's AppData node instead.
export interface PatchCustomerAppDataPayload {
  user?: { email?: string; phone?: string };
  appData?: {
    engagement?: AppEngagement;
    contactTopics?: string[];
  };
}

const patchCustomerAppData = async (
  payload: PatchCustomerAppDataPayload,
  credentials: CustomerCredentials,
): Promise<void> => {
  const signedRequest = await signApiRequest(
    "PATCH",
    `${import.meta.env.VITE_API_URL}/user/user?fid=${credentials.identityId}`,
    credentials,
    { "content-type": "application/json" },
    payload as Record<string, unknown>,
  );
  await apiClient(signedRequest);
};

export const usePatchCustomerAppData = (
  credentials: CustomerCredentials,
  queryClient: QueryClient,
) =>
  useMutation<void, Error, PatchCustomerAppDataPayload>({
    mutationFn: (payload) => patchCustomerAppData(payload, credentials),
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["get-customer-info-by-id"] });
    },
    onError: (error) => {
      // The raw axios error carries email/phone in config.data, so send a sanitized one.
      const status = isAxiosError(error) ? error.response?.status : undefined;
      captureException(
        new Error(
          `patchCustomerAppData failed${status ? ` (status ${status})` : ""}`,
        ),
        {
          tags: {
            operation: "patch-customer-app-data",
            errorType: error.name || "UnknownError",
          },
          extra: {
            apiEndpoint: "/user/user?fid=${identityId}",
            fid: credentials.identityId,
            status,
          },
          level: "error",
        },
      );
    },
  });
